import React, { useMemo } from 'react';
import { Autocomplete, TextField, Checkbox } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const POSITION_GROUPS = [
  {
    id: '6f2689d3b1784a19b10a9ba70601d083',
    name: 'Local Chapter Officers',
  },
  {
    id: '03fdb191d5c54f16b4fdde93cf09b5d6',
    name: 'S/P/D Chapter Officers',
  },
  {
    id: 'e29e072b8ff2421fa7124086f5d4d6ef',
    name: 'S/P/D Assistant Committee',
  },
  {
    id: 'bcf2bce0183b42cbba622da5494ddddb',
    name: 'S/P/D Amendments Committee',
  },
  {
    id: 'd58505b64d9e4638a4fa3ef3a0c9d9d1',
    name: 'S/P/D Convention Committee',
  },
  {
    id: 'dc8a3a9d8b7c4fc5bfc231c9ab618a1a',
    name: 'S/P/D Cottey Committee',
  },
  {
    id: '0e48649906f44975a58e00bf820f5bfd',
    name: 'S/P/D ELF Committee',
  },
  {
    id: '3ce4f8c2baf34c6e98fe2fc52a8de5d0',
    name: 'S/P/D Finance/Audit Committee',
  },
  {
    id: '07a63ca205604be0909fa40336c8c8dc',
    name: 'S/P/D History Committee',
  },
  {
    id: 'f1119d87c91c4f2fb60a05d5cd713043',
    name: 'S/P/D IPS Committee',
  },
  {
    id: 'ed693bf283c84a65b2184fa828ef2993',
    name: 'S/P/D Membership Committee',
  },
  {
    id: '3ff2b300b71d4f678d9dd59c17decc3a',
    name: 'S/P/D Nominating Committee',
  },
  {
    id: '6dfc60ded6a94845955e305f36a7c293',
    name: 'S/P/D PCE Committee',
  },
  {
    id: '488e669164ba4cccba6e9dd7c50a54aa',
    name: 'S/P/D PSA Committee',
  },
  {
    id: 'f07fccc39b344bb794b51d5d1d7d5f77',
    name: 'S/P/D Projects Committee',
  },
  {
    id: 'abddc32040d04901aa41a9832087eb14',
    name: 'S/P/D Reciprocity Committee',
  },
  {
    id: 'ad1b573b6ec248de9bcdd4c7195d259e',
    name: 'S/P/D Quickbooks Committee',
  },
  {
    id: '797d366020084626a209845b18017947',
    name: 'S/P/D STAR Committee',
  },
  {
    id: 'be753b7ba8e94518b8afd476c89b6e70',
    name: 'S/P/D Technology Committee',
  },
  {
    id: '2f0c6a579dfe4f2d984c7c95f01f2ae7',
    name: 'S/P/D Unaffiliated Committee',
  },
  {
    id: 'ade3fc1e3e8e487ea05e1cd619e1cc25',
    name: 'S/P/D Vitality Committee',
  },
  {
    id: '8d352c6f74b4498bb471fb609d582558',
    name: 'Executive Board',
  },
  {
    id: 'd9653976a8fa4524af702e5d9cbb950e',
    name: 'International Past Presidents',
  },
  {
    id: 'af1ef8d3dac94fe5890be291cb95cf21',
    name: 'Local Project Chairs',
  },
  {
    id: '5baa9e7e4ccf4a5ca2b60dd107f7e052',
    name: 'Reciprocities',
  },
  {
    id: 'leadership_team',
    name: 'Leadership Team',
  },
  {
    id: 'past_state_presidents',
    name: 'Past State Presidents',
  },
  {
    id: 'past_state_presidents_residing',
    name: 'Past State Presidents Residing',
  },
];

interface PositionGroup {
  id: string;
  name: string;
}

interface LeadershipPositionGroupsFilterProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  /** Option ids that cannot be selected (e.g. org-wide incompatible groups). */
  disabledOptionIds?: string[];
}

const LeadershipPositionGroupsFilter: React.FC<
  LeadershipPositionGroupsFilterProps
> = ({
  value,
  onChange,
  disabled = false,
  required = false,
  disabledOptionIds,
}) => {
  const disabledSet = useMemo(
    () => new Set(disabledOptionIds ?? []),
    [disabledOptionIds]
  );

  const selected = useMemo(() => {
    if (!value) return [];
    const ids = value
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
    return POSITION_GROUPS.filter((g) => ids.includes(g.id));
  }, [value]);

  const handleChange = (_event: any, newValue: PositionGroup[]) => {
    onChange(newValue.map((g) => g.id).join(','));
  };

  return (
    <Autocomplete
      multiple
      id="leadership-position-groups-filter"
      options={POSITION_GROUPS}
      disableCloseOnSelect
      disabled={disabled}
      value={selected}
      onChange={handleChange}
      getOptionLabel={(option) => option.name}
      getOptionDisabled={(option) => disabledSet.has(option.id)}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      renderOption={(props, option, { selected: sel }) => (
        <li {...props}>
          <Checkbox
            icon={icon}
            checkedIcon={checkedIcon}
            style={{ marginRight: 8 }}
            checked={sel}
          />
          {option.name}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Position groups"
          placeholder="Select groups..."
          required={required}
          size="small"
        />
      )}
    />
  );
};

export default LeadershipPositionGroupsFilter;
